import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { updateClientActivity } from '@/services/magicLinkService';

/**
 * Hook personnalisé pour tracker l'activité d'un client authentifié
 * 
 * Fonctionnalités :
 * - Met à jour last_accessed_at à chaque changement de page ou rechargement
 * - Throttling pour éviter les appels trop fréquents (minimum 30 secondes)
 * 
 * @param isAuthenticated - Indique si le client est authentifié
 * @param clientId - ID du client (peut être null)
 * @param email - Email de l'utilisateur actuellement connecté (peut être null)
 */
export const useActivityTracker = (isAuthenticated: boolean, clientId: string | null, email: string | null) => {
  const location = useLocation();
  const lastUpdateRef = useRef<number>(0);
  
  // Fonction pour obtenir le token et email stockés
  const getStoredAuth = (): { token: string; email: string } | null => {
    if (!clientId) return null;
    
    try {
      const storedAuth = localStorage.getItem(`client_auth_${clientId}`);
      if (storedAuth) {
        const authData = JSON.parse(storedAuth);
        if (authData.token && authData.email) {
          return { token: authData.token, email: authData.email };
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des infos d\'auth:', error);
    }
    
    return null;
  };
  
  // Fonction pour mettre à jour l'activité (avec throttling)
  const updateActivity = async () => {
    if (!isAuthenticated || !clientId || !email) {
      return;
    }
    
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateRef.current;
    
    // Throttling : ne pas mettre à jour si moins de 30 secondes depuis la dernière mise à jour
    if (timeSinceLastUpdate < 30000) {
      return;
    }
    
    const authData = getStoredAuth();
    if (!authData) {
      return;
    }
    
    try {
      const result = await updateClientActivity(authData.token, authData.email);
      if (result.success) {
        lastUpdateRef.current = now;
      }
    } catch (error) {
      console.error('[ActivityTracker] ❌ Erreur lors de la mise à jour:', error);
    }
  };
  
  // Mise à jour à chaque changement de page
  useEffect(() => {
    if (isAuthenticated && clientId && email) {
      updateActivity();
    }
  }, [location.pathname, isAuthenticated, clientId, email]);
  
  // Pas de retour nécessaire, le hook fonctionne en arrière-plan
};

